import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Autocomplete, TextField } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { Loader } from '@/components/loader/loader';
import { LightUserDto, UserDto } from '@cloudcost/shared-types/users';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { DisputeDialog } from '@/components/dispute-dialog/dispute-dialog';
import DisputesModal from '@/components/disputes-table/disputes-modal';
import { getUser } from '@/lib/common';
import ResourceDetailsModal from '@/components/resource-details/resource-details-modal';

type AllocationFilterMode = 'allUnassigned' | 'missingOwner' | 'missingSystem' | 'all';

interface AllocationFilterCounts {
  all: number;
  allUnassigned: number;
  missingOwner: number;
  missingSystem: number;
}

interface ResourceAllocationProps {
  onAssignmentChange?: () => void;
  filterMode: AllocationFilterMode;
  onFilterModeChange: (mode: AllocationFilterMode) => void;
  onFilterCountsChange: (counts: AllocationFilterCounts) => void;
  forecastByResourceId?: Map<number, number>;
}

export type { ResourceAllocationProps };

// Row grid: (checkbox) · Resource · Type · Forecast · System · Owner
const GRID_COLS = '26px 1.4fr 1fr 90px 1fr 1fr';

const FILTERS: {
  id: AllocationFilterMode;
  label: string;
  countKey: keyof AllocationFilterCounts;
}[] = [
  { id: 'allUnassigned', label: 'Unassigned', countKey: 'allUnassigned' },
  { id: 'missingOwner', label: 'Missing owner', countKey: 'missingOwner' },
  { id: 'missingSystem', label: 'Missing system', countKey: 'missingSystem' },
  { id: 'all', label: 'All', countKey: 'all' },
];

// Design-spec 18×18 checkbox (checked = filled primary + white ✓; indeterminate
// = filled primary + white dash; unchecked = white with #C6CFDC border).
function CheckBox({
  checked,
  indeterminate,
  onClick,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  const filled = checked || indeterminate;
  return (
    <span
      onClick={onClick}
      style={{
        width: 18,
        height: 18,
        borderRadius: 5,
        boxSizing: 'border-box',
        border: `2px solid ${filled ? '#1C71E3' : '#C6CFDC'}`,
        background: filled ? '#1C71E3' : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        cursor: 'pointer',
      }}>
      {checked ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12l5 5L20 6"
            stroke="#fff"
            strokeWidth="3.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : indeterminate ? (
        <span style={{ width: 8, height: 2, background: '#fff', borderRadius: 1 }} />
      ) : null}
    </span>
  );
}

const pickerSx = {
  flex: 1,
  minWidth: 150,
  '& .MuiOutlinedInput-root': {
    height: 44,
    borderRadius: '10px',
    background: '#fff',
    fontSize: 14,
    '& fieldset': { borderColor: '#E1E7F0' },
    '&:hover fieldset': { borderColor: '#C3D0E2' },
    '&.Mui-focused fieldset': {
      borderColor: '#1C71E3',
      boxShadow: '0 0 0 3px rgba(28,113,227,.12)',
    },
  },
} as const;

function ResourceAllocation({
  onAssignmentChange,
  filterMode,
  onFilterModeChange,
  onFilterCountsChange,
  forecastByResourceId,
}: ResourceAllocationProps) {
  const location = useLocation();

  const [users, setUsers] = useState<LightUserDto[]>([]);
  const [allItems, setAllItems] = useState<any[]>([]);
  const [allSystems, setAllSystems] = useState<any[]>([]);
  const [resourceGroups, setResourceGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [formOwner, setFormOwner] = useState<any>(null);
  const [systemPicked, setSystemPicked] = useState<any>(null);

  const [searchInput, setSearchInput] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [expandedSubscriptions, setExpandedSubscriptions] = useState<
    Map<string, boolean>
  >(new Map());
  const [expandedResourceGroups, setExpandedResourceGroups] = useState<
    Map<string, boolean>
  >(new Map());
  const [assignedNotice, setAssignedNotice] = useState(0);

  // Dispute + resource-detail modals (preserved functionality)
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [dispute, setDispute] = useState<any[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserDto[]>([]);
  const [defaultReceiverId, setDefaultReceiverId] = useState<number | null>(null);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);

  // Debounced search -> resourceFilter
  useEffect(() => {
    const t = setTimeout(() => setResourceFilter(searchInput), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [userData, resourceData, systemsData, resourceGroupsData] =
          await Promise.all([
            fetchWrapper.get(urls.DevOps.getUsersTech),
            fetchWrapper.get(urls.DevOps.allresources),
            fetchWrapper.get(urls.DevOps.allsystems),
            fetchWrapper.get(urls.DevOps.resourcegroups),
          ]);
        if (!isMounted) return;
        setUsers([...userData, { id: -1, username: 'None (deallocate)' }]);
        setAllItems(resourceData);
        setAllSystems([...systemsData, { id: -1, name: 'None (deallocate)' }]);
        setResourceGroups(resourceGroupsData);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  // Resources visible under the active filter tab.
  const filteredItems = useMemo(() => {
    return allItems.filter((item: any) => {
      const hasOwner = item.id_user_assigned_owner != null;
      const hasSystem = item.id_system != null;
      if (filterMode === 'all') return true;
      if (filterMode === 'allUnassigned') return !hasOwner || !hasSystem;
      if (filterMode === 'missingOwner') return !hasOwner;
      if (filterMode === 'missingSystem') return !hasSystem;
      return true;
    });
  }, [allItems, filterMode]);

  // subscription -> resource-group-id -> resources
  const resourcesMap = useMemo(() => {
    const map = new Map<string, Map<any, any[]>>();
    const rgById = new Map<any, any>();
    resourceGroups.forEach((rg: any) => rgById.set(rg.id, rg));
    for (const item of filteredItems) {
      const rgId = item.resource_group_id ?? item.id_resource_group;
      const rg = rgId != null ? rgById.get(rgId) : null;
      const subKey = rg?.subscription_name ?? item.subscription_name ?? 'Unassigned';
      if (!map.has(subKey)) map.set(subKey, new Map());
      const rgKey = rgId ?? item.resource_group_name;
      if (!map.get(subKey)!.has(rgKey)) map.get(subKey)!.set(rgKey, []);
      map.get(subKey)!.get(rgKey)!.push(item);
    }
    return map;
  }, [filteredItems, resourceGroups]);

  const rowForecast = useCallback(
    (r: any) =>
      forecastByResourceId
        ? forecastByResourceId.get(r.id) ?? 0
        : Number(r.cost ?? 0),
    [forecastByResourceId],
  );

  const getGroupsForSub = useCallback(
    (subscription: string) => {
      const lc = resourceFilter.toLowerCase();
      const groups = resourceGroups
        .filter((rg: any) => rg.subscription_name === subscription)
        .map((rg: any) => {
          const resources = resourcesMap.get(subscription)?.get(rg.id) ?? [];
          // A search matching the subscription or resource-group NAME keeps all
          // its resources, so the tree body agrees with the tab counts (which
          // also match on sub/RG name) instead of rendering an empty tree.
          const nameMatch =
            !lc ||
            subscription.toLowerCase().includes(lc) ||
            (rg.resource_group_name?.toLowerCase().includes(lc) ?? false);
          const fr = resources
            .filter(
              (r: any) =>
                nameMatch ||
                r.resource_name?.toLowerCase().includes(lc) ||
                r.resource_type?.toLowerCase().includes(lc) ||
                r.name?.toLowerCase().includes(lc) ||
                r.username?.toLowerCase().includes(lc),
            )
            .sort((a: any, b: any) => rowForecast(b) - rowForecast(a));
          const cost = fr.reduce((s: number, r: any) => s + rowForecast(r), 0);
          return { ...rg, resources: fr, cost };
        })
        .filter((rg: any) => rg.resources.length > 0)
        .sort((a: any, b: any) => (b.cost ?? 0) - (a.cost ?? 0));
      const totalCost = groups.reduce(
        (s: number, rg: any) => s + Number(rg.cost ?? 0),
        0,
      );
      return { groups, totalCost };
    },
    [resourceGroups, resourcesMap, resourceFilter, rowForecast],
  );

  const subscriptions = useMemo(() => {
    if (resourceGroups.length === 0) return [] as string[];
    const names = [
      ...new Set(resourceGroups.map((rg: any) => rg.subscription_name)),
    ] as string[];
    return names
      .filter((n) => getGroupsForSub(n).groups.length > 0)
      .sort((a, b) => getGroupsForSub(b).totalCost - getGroupsForSub(a).totalCost);
  }, [resourceGroups, getGroupsForSub]);

  const filterCounts = useMemo<AllocationFilterCounts>(() => {
    const lower = resourceFilter.trim().toLowerCase();
    const rgById = new Map<any, any>();
    resourceGroups.forEach((g: any) => rgById.set(g.id, g));
    const matches = (r: any) => {
      if (!lower) return true;
      const rgId = r.id_resource_group ?? r.resource_group_id;
      const rg = rgById.get(rgId);
      return [
        r.resource_name,
        r.resource_type,
        r.name,
        r.username,
        rg?.subscription_name,
        rg?.name,
        r.resource_group_name,
      ].some((v) => v?.toString().toLowerCase().includes(lower));
    };
    let all = 0,
      allUnassigned = 0,
      missingOwner = 0,
      missingSystem = 0;
    allItems.forEach((r: any) => {
      if (!matches(r)) return;
      const hasOwner = r.id_user_assigned_owner != null;
      const hasSystem = r.id_system != null;
      all += 1;
      if (!hasOwner || !hasSystem) allUnassigned += 1;
      if (!hasOwner) missingOwner += 1;
      if (!hasSystem) missingSystem += 1;
    });
    return { all, allUnassigned, missingOwner, missingSystem };
  }, [allItems, resourceFilter, resourceGroups]);

  useEffect(() => {
    onFilterCountsChange(filterCounts);
  }, [filterCounts, onFilterCountsChange]);

  // Switching the filter tab (locally or from the action center) clears the
  // selection and both pickers.
  useEffect(() => {
    setSelectedIds(new Set());
    setFormOwner(null);
    setSystemPicked(null);
  }, [filterMode]);

  const selectedResources = useMemo(
    () => allItems.filter((r: any) => selectedIds.has(r.id)),
    [allItems, selectedIds],
  );
  const totalSelected = selectedIds.size;
  const canAssign = totalSelected > 0 && (!!formOwner?.id || !!systemPicked?.id);
  const canDispute = totalSelected > 0;

  const toggleSub = (name: string) =>
    setExpandedSubscriptions((prev) => {
      const m = new Map(prev);
      m.set(name, !m.get(name));
      return m;
    });
  const toggleGroupExpand = (id: any) =>
    setExpandedResourceGroups((prev) => {
      const m = new Map(prev);
      m.set(id, !m.get(id));
      return m;
    });

  const toggleResource = (id: number) =>
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  const toggleGroup = (resources: any[], allSel: boolean) =>
    setSelectedIds((prev) => {
      const s = new Set(prev);
      resources.forEach((r) => {
        if (allSel) s.delete(r.id);
        else s.add(r.id);
      });
      return s;
    });

  const handleAssign = useCallback(async () => {
    const resourceIds = [...selectedIds];
    if (resourceIds.length === 0) return;
    try {
      if (formOwner?.id) {
        await fetchWrapper.post(urls.DevOps.assignowner, {
          user: formOwner.id,
          resourceIds,
        });
      }
      if (systemPicked?.id) {
        await fetchWrapper.post(urls.DevOps.assignSystem, {
          system: systemPicked.id,
          resourceIds,
        });
      }
      setAssignedNotice(resourceIds.length);
      setTimeout(() => setAssignedNotice(0), 4000);

      const updated = await fetchWrapper.get(urls.DevOps.allresources);
      setAllItems(updated);
      setSelectedIds(new Set());
      setFormOwner(null);
      setSystemPicked(null);
      setExpandedSubscriptions(new Map());
      setExpandedResourceGroups(new Map());
      onAssignmentChange?.();
    } catch (error) {
      console.error('Error assigning:', error);
    }
  }, [selectedIds, formOwner, systemPicked, onAssignmentChange]);

  const handleOpenDispute = useCallback(async () => {
    if (selectedResources.length === 0) return;
    try {
      const us = await fetchWrapper.get(urls.DevOps.getUsersAll);
      setAvailableUsers(us);
      const ownerIds = selectedResources
        .map((r: any) => r.owner_id || r.assigned_owner_id)
        .filter((id: any) => id != null && id !== -1);
      if (ownerIds.length === 0) {
        setDefaultReceiverId(null);
        setShowUserSelector(true);
      } else {
        const uniq = [...new Set(ownerIds)];
        if (uniq.length === 1) {
          setDefaultReceiverId(uniq[0] as number);
          setShowUserSelector(false);
        } else {
          setDefaultReceiverId(null);
          setShowUserSelector(true);
        }
      }
      setDisputeDialogOpen(true);
    } catch (error) {
      console.error('Error opening dispute dialog:', error);
    }
  }, [selectedResources]);

  const handleDisputeSubmit = useCallback(
    async (title: string, receiverId: number) => {
      const resourceIds = selectedResources.map((r: any) => r.id);
      if (resourceIds.length === 0) return;
      try {
        const user = getUser();
        await fetchWrapper.post(urls.DevOps.createDisputeV2, {
          id_receiver: receiverId,
          sender: user?.username,
          title,
          type: 'Resource',
          resource_ids: resourceIds,
          action_center_id: null,
        });
        const maxDisputeData = await fetchWrapper.get(urls.DevOps.getMaxDispute);
        const disputeId = maxDisputeData[0]?.disputeid;
        if (disputeId) {
          const disputesList = await fetchWrapper.get(urls.DevOps.disputesList);
          const created = disputesList.filter((d: any) => d.id === disputeId);
          if (created.length > 0) {
            setDispute(created);
            setDisputeDialogOpen(false);
            setDisputeModalOpen(true);
            setSelectedIds(new Set());
          }
        }
      } catch (error) {
        console.error('Error creating dispute:', error);
      }
    },
    [selectedResources],
  );

  const handleResourceClick = (id: number) => {
    setSelectedResourceId(id);
    setResourceModalOpen(true);
  };

  const user = getUser();

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <div
        style={{
          background: '#fff',
          border: '1px solid #E7ECF3',
          borderRadius: 14,
          boxShadow: '0 1px 2px rgba(16,35,58,.04)',
          overflow: 'hidden',
          fontFamily: 'Lato, sans-serif',
        }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            padding: '16px 20px',
            borderBottom: '1px solid #EEF1F6',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: '#EAF2FD',
                color: '#1C71E3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
              }}>
              <AssignmentOutlinedIcon sx={{ fontSize: 18 }} />
            </span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, color: '#16233A' }}>
                Owner &amp; system assignment
              </div>
              <div style={{ fontSize: 12.5, color: '#8A94A6' }}>
                Select resources — a whole group or individually — then assign an
                owner and/or system
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}>
            {/* Segmented filter */}
            <div
              style={{
                display: 'flex',
                gap: 5,
                padding: 3,
                background: '#F3F6FA',
                border: '1px solid #E7ECF3',
                borderRadius: 9,
              }}>
              {FILTERS.map((f) => {
                const active = filterMode === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => onFilterModeChange(f.id)}
                    style={{
                      padding: '7px 13px',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 12.5,
                      cursor: 'pointer',
                      fontFamily: 'Lato, sans-serif',
                      background: active ? '#1C71E3' : 'transparent',
                      color: active ? '#fff' : '#5A6472',
                    }}>
                    {f.label} ({filterCounts[f.countKey]})
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search resources…"
              style={{
                width: 210,
                padding: '9px 13px',
                borderRadius: 9,
                border: '1px solid #E1E7F0',
                fontSize: 13,
                fontFamily: 'Lato, sans-serif',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#1C71E3';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(28,113,227,.12)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#E1E7F0';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Column header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: GRID_COLS,
            gap: 12,
            padding: '9px 20px 9px 56px',
            fontSize: 10.5,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '.4px',
            color: '#9BA6B7',
          }}>
          <span />
          <span>Resource</span>
          <span>Type</span>
          <span style={{ textAlign: 'right' }}>Forecast</span>
          <span>System</span>
          <span>Owner</span>
        </div>

        {/* Tree body */}
        <div
          style={{
            maxHeight: 440,
            overflowY: 'auto',
            borderTop: '1px solid #E7ECF3',
          }}>
          {subscriptions.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '50px 20px',
                fontSize: 13.5,
                fontWeight: 700,
                color: '#9BA6B7',
              }}>
              No resources match this filter.
            </div>
          ) : (
            subscriptions.map((sub) => {
              const { groups } = getGroupsForSub(sub);
              const subExpanded = expandedSubscriptions.get(sub) ?? false;
              const visibleCount = groups.reduce(
                (s: number, g: any) => s + g.resources.length,
                0,
              );
              return (
                <div key={sub}>
                  {/* Subscription row */}
                  <div
                    onClick={() => toggleSub(sub)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '11px 20px',
                      background: '#F0F4FA',
                      cursor: 'pointer',
                      borderTop: '1px solid #E7ECF3',
                    }}>
                    <span style={{ fontSize: 12, color: '#5A6472', width: 12 }}>
                      {subExpanded ? '▾' : '▸'}
                    </span>
                    <FolderOutlinedIcon sx={{ fontSize: 17, color: '#1C71E3' }} />
                    <span
                      style={{
                        fontSize: 13.5,
                        fontWeight: 800,
                        color: '#16233A',
                        flex: 1,
                      }}>
                      {sub}
                    </span>
                    <span
                      style={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: '#8A94A6',
                        background: '#fff',
                        border: '1px solid #E1E7F0',
                        borderRadius: 999,
                        padding: '2px 9px',
                      }}>
                      {visibleCount}
                    </span>
                  </div>

                  {subExpanded &&
                    groups.map((g: any) => {
                      const gKey = g.id ?? `${sub}-${g.resource_group_name}`;
                      const gExpanded = expandedResourceGroups.get(g.id) ?? false;
                      const allSel =
                        g.resources.length > 0 &&
                        g.resources.every((r: any) => selectedIds.has(r.id));
                      const someSel = g.resources.some((r: any) =>
                        selectedIds.has(r.id),
                      );
                      return (
                        <div key={gKey}>
                          {/* Resource-group row */}
                          <div
                            onClick={() => toggleGroupExpand(g.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '10px 20px 10px 38px',
                              background: '#FAFBFD',
                              cursor: 'pointer',
                              borderTop: '1px solid #EEF1F6',
                            }}>
                            <CheckBox
                              checked={allSel}
                              indeterminate={someSel && !allSel}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleGroup(g.resources, allSel);
                              }}
                            />
                            <span
                              style={{ fontSize: 12, color: '#5A6472', width: 12 }}>
                              {gExpanded ? '▾' : '▸'}
                            </span>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: '#3D4A5C',
                                flex: 1,
                              }}>
                              {g.resource_group_name}
                            </span>
                            <span
                              style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: '#8A94A6',
                              }}>
                              {g.resources.length} resources
                            </span>
                          </div>

                          {gExpanded &&
                            g.resources.map((r: any) => {
                              const sel = selectedIds.has(r.id);
                              const hasOwner = r.id_user_assigned_owner != null;
                              const hasSystem = r.id_system != null;
                              const forecast = forecastByResourceId
                                ? forecastByResourceId.get(r.id)
                                : r.cost == null || r.cost === 'n/a'
                                  ? null
                                  : Number(r.cost);
                              return (
                                <div
                                  key={r.id}
                                  onClick={() => toggleResource(r.id)}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: GRID_COLS,
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: '11px 20px 11px 56px',
                                    cursor: 'pointer',
                                    borderTop: '1px solid #EEF1F6',
                                    background: sel ? '#F2F8FF' : 'transparent',
                                  }}>
                                  <CheckBox
                                    checked={sel}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleResource(r.id);
                                    }}
                                  />
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResourceClick(r.id);
                                    }}
                                    title={r.resource_name}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.textDecoration =
                                        'underline')
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.textDecoration = 'none')
                                    }
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: '#16233A',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      cursor: 'pointer',
                                    }}>
                                    {r.resource_name}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 12.5,
                                      color: '#5A6472',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}>
                                    {r.resource_type}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 12.5,
                                      fontWeight: 800,
                                      color: '#16233A',
                                      textAlign: 'right',
                                    }}>
                                    {forecast == null
                                      ? ''
                                      : formatCurrencyNoUnit(Number(forecast))}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 12.5,
                                      fontWeight: 700,
                                      color: hasSystem ? '#16233A' : '#E23D3D',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}>
                                    {hasSystem ? r.name : 'Unassigned'}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 12.5,
                                      fontWeight: 700,
                                      color: hasOwner ? '#16233A' : '#E23D3D',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}>
                                    {hasOwner ? r.username : 'Unassigned'}
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      );
                    })}
                </div>
              );
            })
          )}
        </div>

        {/* Success banner */}
        {assignedNotice > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '10px 20px',
              background: '#E5F6EC',
              color: '#16A55C',
              fontWeight: 700,
              fontSize: 13,
              borderTop: '1px solid rgba(22,165,92,.25)',
            }}>
            ✓ Assigned {assignedNotice} resource{assignedNotice === 1 ? '' : 's'}.
          </div>
        )}

        {/* Assignment bar (footer) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '15px 20px',
            borderTop: '1px solid #E7ECF3',
            background: '#FCFDFF',
            flexWrap: 'wrap',
          }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: '#16233A',
              minWidth: 82,
            }}>
            {totalSelected} selected
          </span>
          <Autocomplete
            size="small"
            sx={pickerSx}
            options={users}
            value={formOwner}
            getOptionLabel={(option: any) => option?.username ?? ''}
            onChange={(_e, value) => setFormOwner(value)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Pick owner…" />
            )}
          />
          <Autocomplete
            size="small"
            sx={pickerSx}
            options={allSystems}
            value={systemPicked}
            getOptionLabel={(option: any) => option?.name ?? ''}
            onChange={(_e, value) => setSystemPicked(value)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Pick system…" />
            )}
          />
          <button
            type="button"
            onClick={handleOpenDispute}
            disabled={!canDispute}
            style={{
              height: 44,
              padding: '0 18px',
              borderRadius: 10,
              border: '1px solid #E1E7F0',
              background: '#fff',
              color: canDispute ? '#1C71E3' : '#B7C4D6',
              fontWeight: 700,
              fontSize: 14,
              cursor: canDispute ? 'pointer' : 'not-allowed',
              fontFamily: 'Lato, sans-serif',
              flex: 'none',
            }}>
            Dispute
          </button>
          <button
            type="button"
            onClick={handleAssign}
            disabled={!canAssign}
            style={{
              height: 44,
              padding: '0 22px',
              borderRadius: 10,
              border: 'none',
              fontWeight: 800,
              fontSize: 14,
              color: '#fff',
              background: canAssign ? '#1C71E3' : '#B7C4D6',
              cursor: canAssign ? 'pointer' : 'not-allowed',
              fontFamily: 'Lato, sans-serif',
              flex: 'none',
            }}>
            Assign
          </button>
        </div>
      </div>

      <DisputeDialog
        open={disputeDialogOpen}
        onClose={() => setDisputeDialogOpen(false)}
        onSubmit={handleDisputeSubmit}
        defaultReceiverId={defaultReceiverId}
        currentUserId={user?.id || 0}
        showUserSelector={showUserSelector}
        availableUsers={availableUsers}
      />

      {disputeModalOpen && dispute[0] && (
        <DisputesModal
          Dispute={dispute[0]}
          IsModalOpened={disputeModalOpen}
          onCloseModal={() => {
            setDisputeModalOpen(false);
            setDispute([]);
          }}
        />
      )}

      <ResourceDetailsModal
        open={resourceModalOpen}
        onClose={() => {
          setResourceModalOpen(false);
          setSelectedResourceId(null);
        }}
        resourceId={selectedResourceId}
        currency={user?.default_currency || 'USD'}
      />
    </>
  );
}

export default ResourceAllocation;
